import { type InitFlowComponent, useIntl } from '@capsicum/common';
import { Button, Chip, SkeletonItem, Tooltip } from '@capsicum/common-web';
import { QuestPostIcon, TopUpIcon } from '@capsicum/common-web/icons';
import { useInterestsFlow } from '@capsicum/features/onboard/shared';
import { Shell } from '@capsicum/socios-core-web';
import { useEffect, useMemo } from 'react';
import styled, { useTheme } from 'styled-components';

export const InterestsFlow: InitFlowComponent = ({ fallback, isReactive, next }) => {
	const { $t } = useIntl();
	const { colors } = useTheme();
	const { categories, findTranslationKey, isLoading, isNextStepEnabled, isVisible, onContinue, onSetChipCallback } =
		useInterestsFlow();

	const renderCategories = useMemo(() => {
		if (isLoading) {
			return (
				<S.SkeletonWrapper>
					{Array(10)
						.fill(null)
						.map((_, index) => (
							<SkeletonItem height={50} key={index} width="100%" />
						))}
				</S.SkeletonWrapper>
			);
		}

		return categories?.map(category => {
			const hasDescription = Boolean(category.description);

			return (
				<S.Category key={category.id}>
					<S.CategoryHeader>
						<S.CategoryName>{findTranslationKey('name', category)}</S.CategoryName>
						{hasDescription ? (
							<Tooltip contentPosition="bottom">
								<Tooltip.Trigger testID={`interest-${category.name}-tooltip`}>
									<QuestPostIcon color={colors.icons.brand.enabled}></QuestPostIcon>
								</Tooltip.Trigger>
								<Tooltip.Content>
									<Tooltip.Description>
										<p>{findTranslationKey('description', category)}</p>
									</Tooltip.Description>
								</Tooltip.Content>
							</Tooltip>
						) : null}
					</S.CategoryHeader>
					<S.CategoryTypes>
						{category.interests &&
							category?.interests?.map(interest => {
								return (
									<Chip
										isSelected={false}
										key={interest.id}
										testID={`interest-chip-${interest.name}`}
										text={findTranslationKey('name', interest)}
										value={interest.id}
										variant="background"
										onClick={onSetChipCallback}
									/>
								);
							})}
					</S.CategoryTypes>
				</S.Category>
			);
		});
	}, [onSetChipCallback, categories, isLoading, findTranslationKey]);

	useEffect(() => {
		if (!isReactive) next();
	}, [isReactive, next]);

	return isVisible === false ? (
		fallback
	) : (
		<S.Wrapper>
			{isVisible && (
				<Shell
					buttons={[
						<Button
							disabled={!isNextStepEnabled}
							fullWidth
							key="confirm"
							testID="interests-continue-button"
							text={$t('onboard.interestsScreen.button')}
							onClick={onContinue}
						/>
					]}
					showLogo={false}
				>
					<S.Content>
						<S.Header>
							<S.TopHeading>{$t('onboard.interestsScreen.topHeading')}</S.TopHeading>
							<S.Title>{$t('onboard.interestsScreen.heading')}</S.Title>
							<S.SubHeading>{$t('onboard.interestsScreen.subHeading')}</S.SubHeading>
						</S.Header>

						{renderCategories}
					</S.Content>
				</Shell>
			)}
		</S.Wrapper>
	);
};

InterestsFlow.keepAlive = true;

namespace S {
	export const Wrapper = styled.div`
		height: 100%;
		display: flex;
		overflow-y: auto;
		align-items: center;
		justify-content: center;
		color: ${({ theme }) => theme.colors.text.main.enabled};
		background-color: ${({ theme }) => theme.colors.backgrounds.main.container};
	`;

	export const Header = styled.header`
		display: flex;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.xxxsmall};
	`;

	export const Title = styled.h2`
		${({ theme }) => theme.fonts.headings.title5};
		margin: 0;
	`;

	export const TopHeading = styled.span`
		${({ theme }) => theme.fonts.text.text1};
	`;

	export const SubHeading = styled.span`
		${({ theme }) => theme.fonts.text.text3};
		color: ${({ theme }) => theme.colors.text.main.subtle};
	`;

	export const Content = styled.div`
		display: flex;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.xlarge};
	`;

	export const InterestsScreen = styled.div``;

	export const Category = styled.div`
		margin-bottom: ${({ theme }) => theme.spacings.large};
	`;

	export const CategoryName = styled.h3`
		${({ theme }) => theme.fonts.text.text6};
		margin: 0;
		padding: 0;
	`;

	export const CategoryTypes = styled.div`
		display: flex;
		flex-wrap: wrap;
		gap: ${({ theme }) => theme.spacings.xsmall};
	`;

	export const CategoryHeader = styled.div`
		margin-bottom: ${({ theme }) => theme.spacings.xsmall};
		gap: ${({ theme }) => theme.spacings.xsmall};
		display: flex;
		padding: 0;
	`;

	export const InfoIconStyled = styled(TopUpIcon)`
		flex-shrink: 0;
	`;

	export const SkeletonWrapper = styled.div`
		display: flex;
		flex-direction: column;
		gap: ${({ theme }) => `${theme.spacings.small} `};
	`;
}
